import type React from "react";
import { useContext } from "react";
import { useDrop } from "react-dnd";
import { GiSpeaker } from "react-icons/gi";
import { LanguageContext, useSpeakingContext } from "../../../../app/context";
import { playSynthesisUtterance } from "../../../../shared/helpers";
import type { WordsAndDefinitionsType } from "../type";

type Props = {
	definition: string;
	onDropWord: (definition: string, item: string) => void;
	droppedWord: string;
	onRemoveWord: (definition: string, word: string) => void;
	correct?: null | boolean;
	matchWithAudio?: boolean;
};

export const Definition: React.FC<Props> = ({
	definition,
	onDropWord,
	droppedWord,
	onRemoveWord,
	correct,
	matchWithAudio,
}) => {
	const { isSpeaking, setIsSpeaking, currentIsClicked, setCurrentIsClicked } =
		useSpeakingContext();
	const [{ isOver }, drop] = useDrop({
		accept: "words-defs",
		drop: (item: WordsAndDefinitionsType) => onDropWord(definition, item.word),
		canDrop: () => !droppedWord,
		collect: (monitor) => ({
			isOver: !!monitor.isOver(),
		}),
	});

	const { language } = useContext(LanguageContext);

	const prefixClass = "match-words";

	function paintBackground(correct: undefined | null | boolean) {
		if (correct === undefined) {
			return "";
		}
		if (correct === false) {
			return "-incorrect";
		}
		return "-correct";
	}

	return (
		<li className={`${prefixClass}__definition-item`}>
			{matchWithAudio ? (
				<button
					type="button"
					disabled={isSpeaking}
					className={`${prefixClass}__play-sound${
						definition === currentIsClicked && isSpeaking ? "--active" : ""
					}`}
					onClick={() => {
						playSynthesisUtterance({
							word: definition,
							language: language,
							onStart: () => setIsSpeaking(true),
							onEnd: () => setIsSpeaking(false),
						});
						setCurrentIsClicked("");
						setCurrentIsClicked(definition);
					}}
				>
					<GiSpeaker />
				</button>
			) : (
				<p>{definition}</p>
			)}
			<button
				type="button"
				onClick={
					droppedWord ? () => onRemoveWord(definition, droppedWord) : () => {}
				}
				ref={drop}
				className={`${prefixClass}__dropped-word${paintBackground(correct)}`}
				style={{ backgroundColor: isOver ? "lightgray" : "white" }}
			>
				{droppedWord ?? <p>droppedWord</p>}
			</button>
		</li>
	);
};
